import React, { useEffect, useState } from "react";
import { Card, CardBody, CardTitle, Col, Container, Form, FormGroup, Input, Label, Row, Button, Table, Collapse, Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getRoles, saveRole, getRolePermissions, saveRolePermissions, checkRoleNameExists } from "../../components/Api/GmsRole";
import { getPages } from "../../components/Api/GmsPermission";
import { getSidebarName } from "../../components/Permission/PageNameMapper";
import classnames from "classnames";

const DASHBOARD_PAGE_IDS = [9, 44, 45];

const defaultForm = {
  role_Id: null,
  role_Name: "",
  role_Active: true
};

const defaultFilter = {
  role_Name: "",
  role_Active: null
};

export default function RoleManagementPage() {
  const [formData, setFormData] = useState(defaultForm);
  const [filterData, setFilterData] = useState(defaultFilter);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
  const itemsPerPage = 10;
  const [isEditing, setIsEditing] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [availablePages, setAvailablePages] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [permissionMode, setPermissionMode] = useState('A'); // 'A' for full control, 'B' for template
  const [permissionTemplate, setPermissionTemplate] = useState('view-only'); // 'view-only', 'full-access', 'custom'
  const [selectedPages, setSelectedPages] = useState([]);
  const [isCheckingRoleName, setIsCheckingRoleName] = useState(false);
  const [roleNameExists, setRoleNameExists] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.role_Name) {
      validateRoleName();
    }
  }, [formData.role_Name, formData.role_Id]);

  const fetchData = async () => {
    try {
      const pagesData = await getPages();
      const sortedPages = pagesData.sort((a, b) => a.page_NameEn.localeCompare(b.page_NameEn));
      setAvailablePages(sortedPages);

      // Initialize permissions
      setRolePermissions(sortedPages.map(page => ({
        rolePermission_Page_Id: page.page_Id,
        rolePermission_View: false,
        rolePermission_Edit: false,
        rolePermission_Delete: false,
        rolePermission_Approve: 0
      })));

      await loadRoles();
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load initial data",
        icon: "error"
      });
    }
  };

  const loadRoles = async (filter = defaultFilter) => {
    try {
      const data = await getRoles(filter);
      setRoles(data);
      setTotalItems(data.length);
    } catch (error) {
      console.error("Error loading roles:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to load roles",
        icon: "error"
      });
    }
  };

  const validateRoleName = async () => {
    if (!formData.role_Name || !formData.role_Name.trim()) {
      setRoleNameExists(false);
      return;
    }

    try {
      setIsCheckingRoleName(true);
      const exists = await checkRoleNameExists(formData.role_Name.trim(), formData.role_Id);
      setRoleNameExists(exists);
      if (exists) {
        setErrors(prev => ({ ...prev, role_Name: 'Role name already exists among active roles' }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.role_Name;
          return newErrors;
        });
      }
    } catch (error) {
      console.error("Error validating role name:", error);
      setRoleNameExists(false);
    } finally {
      setIsCheckingRoleName(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.role_Name?.trim()) {
      newErrors.role_Name = 'Role name is required';
    } else if (roleNameExists) {
      newErrors.role_Name = 'Role name already exists among active roles';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (name === 'role_Name') {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.role_Name;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await saveRole({
        ...formData,
        role_Name: formData.role_Name.trim()
      });

      if (result.success) {
        // Get role_Id from API response
        const roleIdFromResponse = result.data?.roleId || result.roleId;

        // For editing, use existing roleId; for new role, use the one from response
        const roleIdToUse = isEditing
          ? (formData.role_Id || selectedRoleId)
          : (roleIdFromResponse || formData.role_Id);

        // If permissions are configured (showPermissions is true or permissions are set), save them
        if (roleIdToUse && showPermissions) {
          try {
            let permissionsToSave = [];

            if (permissionMode === 'A') {
              // Mode A: Use the rolePermissions state directly
              permissionsToSave = rolePermissions.filter(p =>
                p.rolePermission_View ||
                p.rolePermission_Edit ||
                p.rolePermission_Delete ||
                p.rolePermission_Approve > 0
              ).map(p => ({
                rolePermission_Role_Id: roleIdToUse,
                rolePermission_Page_Id: p.rolePermission_Page_Id,
                rolePermission_View: p.rolePermission_View,
                rolePermission_Edit: p.rolePermission_Edit,
                rolePermission_Delete: p.rolePermission_Delete,
                rolePermission_Approve: p.rolePermission_Approve
              }));
            } else {
              // Mode B: Apply template to selected pages
              const templateFlags = getTemplateFlags(permissionTemplate);
              permissionsToSave = selectedPages.map(pageId => ({
                rolePermission_Role_Id: roleIdToUse,
                rolePermission_Page_Id: pageId,
                rolePermission_View: templateFlags.view,
                rolePermission_Edit: templateFlags.edit,
                rolePermission_Delete: templateFlags.delete,
                rolePermission_Approve: templateFlags.approve
              }));
            }

            if (permissionsToSave.length > 0) {
              await saveRolePermissions(roleIdToUse, permissionsToSave);
            }
          } catch (error) {
            console.error("Error saving role permissions:", error);
            Swal.fire({
              title: "Warning",
              text: `Role was saved but there was an error saving permissions: ${error.message || 'Unknown error'}`,
              icon: "warning"
            });
          }
        }

        Swal.fire({
          title: "Success!",
          text: isEditing ? "Role updated successfully" : "Role created successfully",
          icon: "success"
        });

        resetForm();
        await loadRoles(filterData);
        // Stay on Add/Edit tab after save
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error("Error saving role:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to save role",
        icon: "error"
      });
    }
  };

  const getTemplateFlags = (template) => {
    switch (template) {
      case 'view-only':
        return { view: true, edit: false, delete: false, approve: 0 };
      case 'full-access':
        return { view: true, edit: true, delete: true, approve: 1 };
      case 'custom':
        return { view: false, edit: false, delete: false, approve: 0 };
      default:
        return { view: false, edit: false, delete: false, approve: 0 };
    }
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setErrors({});
    setIsEditing(false);
    setShowPermissions(false);
    setSelectedRoleId(null);
    setRolePermissions(availablePages.map(page => ({
      rolePermission_Page_Id: page.page_Id,
      rolePermission_View: false,
      rolePermission_Edit: false,
      rolePermission_Delete: false,
      rolePermission_Approve: 0
    })));
    setSelectedPages([]);
    setPermissionMode('A');
    setPermissionTemplate('view-only');
  };

  const handleEdit = async (role) => {
    setFormData({
      role_Id: role.role_Id,
      role_Name: role.role_Name,
      role_Active: role.role_Active
    });
    setIsEditing(true);
    setSelectedRoleId(role.role_Id);
    setActiveTab("1");

    // Load role permissions
    try {
      const perms = await getRolePermissions(role.role_Id);
      const permMap = {};
      perms.forEach(p => {
        permMap[p.rolePermission_Page_Id] = p;
      });

      setRolePermissions(availablePages.map(page => {
        const existing = permMap[page.page_Id];
        return {
          rolePermission_Page_Id: page.page_Id,
          rolePermission_View: existing?.rolePermission_View || false,
          rolePermission_Edit: existing?.rolePermission_Edit || false,
          rolePermission_Delete: existing?.rolePermission_Delete || false,
          rolePermission_Approve: existing?.rolePermission_Approve || 0
        };
      }));

      // Set selected pages for Mode B
      setSelectedPages(perms.map(p => p.rolePermission_Page_Id));
    } catch (error) {
      console.error("Error loading role permissions:", error);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleManagePermissions = async (role) => {
    setSelectedRoleId(role.role_Id);
    setFormData({
      role_Id: role.role_Id,
      role_Name: role.role_Name,
      role_Active: role.role_Active
    });
    setIsEditing(true);
    setActiveTab("1");

    try {
      const perms = await getRolePermissions(role.role_Id);
      const permMap = {};
      perms.forEach(p => {
        permMap[p.rolePermission_Page_Id] = p;
      });

      setRolePermissions(availablePages.map(page => {
        const existing = permMap[page.page_Id];
        return {
          rolePermission_Page_Id: page.page_Id,
          rolePermission_View: existing?.rolePermission_View || false,
          rolePermission_Edit: existing?.rolePermission_Edit || false,
          rolePermission_Delete: existing?.rolePermission_Delete || false,
          rolePermission_Approve: existing?.rolePermission_Approve || 0
        };
      }));

      setSelectedPages(perms.map(p => p.rolePermission_Page_Id));
      setShowPermissions(true);
    } catch (error) {
      console.error("Error loading role permissions:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load role permissions",
        icon: "error"
      });
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePermissionChange = (pageId, field, value) => {
    const isDashboardPage = DASHBOARD_PAGE_IDS.includes(pageId);
    const isGrantingPermission = field === 'rolePermission_Approve' ? Number(value) > 0 : Boolean(value);

    setRolePermissions(prev => {
      let updated = prev.map(p =>
        p.rolePermission_Page_Id === pageId
          ? {
            ...p,
            [field]: field === 'rolePermission_Approve' ? Number(value) : Boolean(value),
            ...(field === 'rolePermission_Edit' && value ? { rolePermission_View: true } : {}),
            ...(field === 'rolePermission_Delete' && value ? { rolePermission_View: true, rolePermission_Edit: true } : {}),
            ...(field === 'rolePermission_Approve' && value ? { rolePermission_View: true, rolePermission_Edit: true, rolePermission_Delete: true } : {})
          }
          : p
      );

      // If a dashboard permission is being granted, uncheck others
      if (isDashboardPage && isGrantingPermission) {
        updated = updated.map(p => {
          if (DASHBOARD_PAGE_IDS.includes(p.rolePermission_Page_Id) && p.rolePermission_Page_Id !== pageId) {
            return {
              ...p,
              rolePermission_View: false,
              rolePermission_Edit: false,
              rolePermission_Delete: false,
              rolePermission_Approve: 0
            };
          }
          return p;
        });
      }

      return updated;
    });
  };

  const handleRowLevelChange = (pageId, checked) => {
    const isDashboardPage = DASHBOARD_PAGE_IDS.includes(pageId);

    setRolePermissions(prev => {
      let updated = prev.map(p =>
        p.rolePermission_Page_Id === pageId
          ? {
            ...p,
            rolePermission_View: Boolean(checked),
            rolePermission_Edit: Boolean(checked),
            rolePermission_Delete: Boolean(checked),
            rolePermission_Approve: Number(checked ? 1 : 0)
          }
          : p
      );

      // If a dashboard is being selected, uncheck others
      if (isDashboardPage && checked) {
        updated = updated.map(p => {
          if (DASHBOARD_PAGE_IDS.includes(p.rolePermission_Page_Id) && p.rolePermission_Page_Id !== pageId) {
            return {
              ...p,
              rolePermission_View: false,
              rolePermission_Edit: false,
              rolePermission_Delete: false,
              rolePermission_Approve: 0
            };
          }
          return p;
        });
      }

      return updated;
    });
  };

  const handleApplyFilter = () => {
    loadRoles(filterData);
  };

  const handleClearFilter = () => {
    setFilterData(defaultFilter);
    loadRoles(defaultFilter);
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilterData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value === '' ? null : value)
    }));
  };

  const handlePageSelect = (selectedOptions) => {
    setSelectedPages(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Refresh roles when switching to View tab
      if (tab === "2") {
        loadRoles(filterData);
      }
      // Reset form when switching to Add/Edit tab
      if (tab === "1") {
        resetForm();
      }
    }
  };

  const isRowFullyChecked = (permission) => {
    return permission.rolePermission_View &&
      permission.rolePermission_Edit &&
      permission.rolePermission_Delete &&
      permission.rolePermission_Approve > 0;
  };

  const filteredRoles = roles.filter(role => {
    if (filterData.role_Name && !role.role_Name.toLowerCase().includes(filterData.role_Name.toLowerCase())) {
      return false;
    }
    if (filterData.role_Active !== null && role.role_Active !== filterData.role_Active) {
      return false;
    }
    return true;
  });

  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="GMS" breadcrumbItem="Role Management" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => toggle("1")}
                    >
                      <i className="bx bx-plus-circle"></i>
                      <span>{isEditing ? 'Edit Role' : 'Add Role'}</span>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>View Roles</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <CardTitle className="mb-0">{isEditing ? 'Edit Role' : 'Create New Role'}</CardTitle>
                    </div>

                    <Form onSubmit={handleSubmit}>
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label>Role Name *</Label>
                            <Input
                              type="text"
                              name="role_Name"
                              value={formData.role_Name}
                              onChange={handleFormChange}
                              invalid={!!errors.role_Name || roleNameExists}
                            />
                            {errors.role_Name && <div className="invalid-feedback d-block">{errors.role_Name}</div>}
                            {isCheckingRoleName && <small className="text-muted">Checking role name...</small>}
                            {!isCheckingRoleName && formData.role_Name && !roleNameExists && !errors.role_Name && (
                              <small className="text-success">Role name is available</small>
                            )}
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>
                              <Input
                                type="checkbox"
                                name="role_Active"
                                checked={formData.role_Active}
                                onChange={handleFormChange}
                              />{' '}
                              Active
                            </Label>
                          </FormGroup>
                        </Col>
                      </Row>

                      {showPermissions && (
                        <Card className="mt-3">
                          <CardBody>
                            <CardTitle className="mb-3">Role Permissions Configuration</CardTitle>

                            <Row className="mb-3">
                              <Col md={12}>
                                <Label>Permission Mode</Label>
                                <div>
                                  <FormGroup check inline>
                                    <Input
                                      type="radio"
                                      name="permissionMode"
                                      value="A"
                                      checked={permissionMode === 'A'}
                                      onChange={(e) => setPermissionMode(e.target.value)}
                                    />
                                    <Label check>Mode A: Full Control (per-page permissions)</Label>
                                  </FormGroup>
                                  <FormGroup check inline className="ml-3">
                                    <Input
                                      type="radio"
                                      name="permissionMode"
                                      value="B"
                                      checked={permissionMode === 'B'}
                                      onChange={(e) => setPermissionMode(e.target.value)}
                                    />
                                    <Label check>Mode B: Template-based</Label>
                                  </FormGroup>
                                </div>
                              </Col>
                            </Row>

                            {permissionMode === 'B' && (
                              <>
                                <Row className="mb-3">
                                  <Col md={12}>
                                    <Label>Permission Template</Label>
                                    <Input
                                      type="select"
                                      value={permissionTemplate}
                                      onChange={(e) => setPermissionTemplate(e.target.value)}
                                    >
                                      <option value="view-only">View Only</option>
                                      <option value="full-access">Full Access</option>
                                      <option value="custom">Custom</option>
                                    </Input>
                                    <small className="text-muted d-block mt-1">
                                      {permissionTemplate === 'view-only' && 'Selected pages will have View permission only'}
                                      {permissionTemplate === 'full-access' && 'Selected pages will have View, Edit, Delete, and Approve permissions'}
                                      {permissionTemplate === 'custom' && 'Selected pages will have no permissions (you can customize after saving)'}
                                    </small>
                                  </Col>
                                </Row>
                                <div className="table-responsive">
                                  <Table className="table-bordered">
                                    <thead>
                                      <tr>
                                        <th className="text-center" style={{ width: '100px' }}>
                                          <div className="d-flex flex-column align-items-center">
                                            <span className="mb-2">Select</span>
                                            <Input
                                              type="checkbox"
                                              checked={availablePages.length > 0 && selectedPages.length === availablePages.length}
                                              onChange={async (e) => {
                                                const checked = e.target.checked;

                                                if (checked) {
                                                  // Identify which dashboards are available in the page list
                                                  const dashboardPagesAvailable = availablePages.filter(p => DASHBOARD_PAGE_IDS.includes(p.page_Id));
                                                  const nonDashboardPageIds = availablePages
                                                    .filter(p => !DASHBOARD_PAGE_IDS.includes(p.page_Id))
                                                    .map(p => p.page_Id);

                                                  if (dashboardPagesAvailable.length > 0) {
                                                    const { value: selectedDashboardId, isDismissed } = await Swal.fire({
                                                      title: 'Select Dashboard',
                                                      text: 'Only one dashboard can be assigned to a role. Please select which one to include in your "Select All" request:',
                                                      input: 'radio',
                                                      inputOptions: dashboardPagesAvailable.reduce((acc, p) => {
                                                        acc[p.page_Id] = getSidebarName(p.page_Id, p.page_NameEn);
                                                        return acc;
                                                      }, { '0': 'None' }),
                                                      inputValue: '0',
                                                      showCancelButton: true,
                                                      confirmButtonText: 'Select All with Chosen Dashboard',
                                                      cancelButtonText: 'Cancel Select All',
                                                      inputValidator: (value) => {
                                                        if (value === undefined || value === null) {
                                                          return 'Please select an option or click cancel';
                                                        }
                                                      }
                                                    });

                                                    if (!isDismissed) {
                                                      const finalSelection = [...nonDashboardPageIds];
                                                      if (selectedDashboardId && selectedDashboardId !== '0') {
                                                        finalSelection.push(parseInt(selectedDashboardId));
                                                      }
                                                      setSelectedPages(finalSelection);
                                                    }
                                                    // if dismissed (cancelled), don't do anything
                                                  } else {
                                                    setSelectedPages(availablePages.map(page => page.page_Id));
                                                  }
                                                } else {
                                                  setSelectedPages([]);
                                                }
                                              }}
                                              style={{ cursor: 'pointer' }}
                                            />
                                          </div>
                                        </th>
                                        <th>Page Name</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {availablePages.map(page => (
                                        <tr key={page.page_Id}>
                                          <td className="text-center">
                                            <Input
                                              type="checkbox"
                                              checked={selectedPages.includes(page.page_Id)}
                                              onChange={(e) => {
                                                const checked = e.target.checked;
                                                const pageId = page.page_Id;

                                                if (checked) {
                                                  if (DASHBOARD_PAGE_IDS.includes(pageId)) {
                                                    // Mutually exclusive dashboards: unselect others when a dashboard is selected
                                                    setSelectedPages(prev => [
                                                      ...prev.filter(id => !DASHBOARD_PAGE_IDS.includes(id)),
                                                      pageId
                                                    ]);
                                                  } else {
                                                    setSelectedPages(prev => [...prev, pageId]);
                                                  }
                                                } else {
                                                  setSelectedPages(prev => prev.filter(id => id !== pageId));
                                                }
                                              }}
                                            />
                                          </td>
                                          <td>{page.page_NameEn}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </>
                            )}

                            {permissionMode === 'A' && (
                              <div className="table-responsive">
                                <Table className="table-bordered">
                                  <thead>
                                    <tr>
                                      <th>Page</th>
                                      <th className="text-center" style={{ width: '100px' }}>All</th>
                                      <th className="text-center" style={{ width: '100px' }}>View</th>
                                      <th className="text-center" style={{ width: '100px' }}>Edit</th>
                                      <th className="text-center" style={{ width: '100px' }}>Delete</th>
                                      <th className="text-center" style={{ width: '100px' }}>Approve</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {availablePages.map(page => {
                                      const permission = rolePermissions.find(p => p.rolePermission_Page_Id === page.page_Id);
                                      return (
                                        <tr key={page.page_Id}>
                                          <td>{page.page_NameEn}</td>
                                          <td className="text-center">
                                            <Input
                                              type="checkbox"
                                              checked={isRowFullyChecked(permission)}
                                              onChange={(e) => handleRowLevelChange(page.page_Id, e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <Input
                                              type="checkbox"
                                              checked={permission?.rolePermission_View || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'rolePermission_View', e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <Input
                                              type="checkbox"
                                              checked={permission?.rolePermission_Edit || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'rolePermission_Edit', e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <Input
                                              type="checkbox"
                                              checked={permission?.rolePermission_Delete || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'rolePermission_Delete', e.target.checked)}
                                            />
                                          </td>
                                          <td className="text-center">
                                            <Input
                                              type="checkbox"
                                              checked={permission?.rolePermission_Approve > 0 || false}
                                              onChange={(e) => handlePermissionChange(page.page_Id, 'rolePermission_Approve', e.target.checked ? 1 : 0)}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      )}

                      <Row>
                        <Col md={12}>
                          {!isEditing && (
                            <Button
                              type="button"
                              color="primary"
                              onClick={() => setShowPermissions(!showPermissions)}
                              className="me-2"
                            >
                              {showPermissions ? 'Hide Permissions' : 'Configure Permissions'}
                            </Button>
                          )}
                          <Button type="submit" color="primary" className="me-2">
                            {isEditing ? 'Update Role' : 'Create Role'}
                          </Button>
                          <Button type="button" color="secondary" onClick={resetForm}>
                            Cancel
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </TabPane>
                  <TabPane tabId="2">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <CardTitle className="mb-0">Roles List</CardTitle>
                      <Button
                        color="link"
                        onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                      >
                        {isFilterCollapsed ? 'Show Filters' : 'Hide Filters'}
                      </Button>
                    </div>

                    <Collapse isOpen={!isFilterCollapsed}>
                      <Card className="mb-3">
                        <CardBody>
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <Label>Role Name</Label>
                                <Input
                                  type="text"
                                  name="role_Name"
                                  value={filterData.role_Name}
                                  onChange={handleFilterChange}
                                  placeholder="Search by role name"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label>Active Status</Label>
                                <Input
                                  type="select"
                                  name="role_Active"
                                  value={filterData.role_Active === null ? '' : filterData.role_Active}
                                  onChange={handleFilterChange}
                                >
                                  <option value="">All</option>
                                  <option value="true">Active</option>
                                  <option value="false">Inactive</option>
                                </Input>
                              </FormGroup>
                            </Col>
                            <Col md={4} className="d-flex align-items-end">
                              <Button color="primary" onClick={handleApplyFilter} className="me-2">
                                Apply Filter
                              </Button>
                              <Button color="secondary" onClick={handleClearFilter}>
                                Clear
                              </Button>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    </Collapse>

                    <div className="table-responsive">
                      <Table className="table-bordered">
                        <thead>
                          <tr>
                            <th>Role Name</th>
                            <th>Active</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedRoles.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="text-center">No roles found</td>
                            </tr>
                          ) : (
                            paginatedRoles.map(role => (
                              <tr key={role.role_Id}>
                                <td>{role.role_Name}</td>
                                <td>{role.role_Active ? 'Yes' : 'No'}</td>
                                <td>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => {
                                      handleEdit(role);
                                      setActiveTab("1");
                                    }}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => {
                                      handleManagePermissions(role);
                                      setActiveTab("1");
                                    }}
                                  >
                                    Permissions
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {filteredRoles.length > itemsPerPage && (
                      <div className="d-flex justify-content-center mt-3">
                        <Button
                          color="primary"
                          size="sm"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          className="me-2"
                        >
                          Previous
                        </Button>
                        <span className="align-self-center mx-3">
                          Page {currentPage} of {Math.ceil(filteredRoles.length / itemsPerPage)}
                        </span>
                        <Button
                          color="primary"
                          size="sm"
                          disabled={currentPage >= Math.ceil(filteredRoles.length / itemsPerPage)}
                          onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredRoles.length / itemsPerPage), prev + 1))}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
